import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons'
import useTimelineV2 from 'hooks/useTimelineV2'
import { useMemo } from 'react'

interface TimelineV2Props {
  patientId: string
}

// Helper function to get human-readable journey names
const getJourneyDisplayName = (machineType: string): string => {
  const displayNames: Record<string, string> = {
    'patient-journey': 'Overall Patient Journey',
    appointment: 'Appointment Process',
    provider: 'Provider Care',
    'post-appointment': 'Post-Appointment Follow-up',
    labs: 'Lab Work',
    'intake-form': 'Intake Forms',
  }
  return (
    displayNames[machineType] ||
    machineType.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  )
}

// Helper function to get human-readable instance names
const getInstanceDisplayName = (machineType: string): string => {
  const displayNames: Record<string, string> = {
    'patient-journey': 'Overall Patient Journey',
    appointment: 'Source Appointment Id',
    provider: 'Provider Elation Id',
    'post-appointment': 'Source Appointment Id',
    labs: 'Lab Order Id',
    'intake-form': 'Source Appointment Id',
  }
  return (
    displayNames[machineType] ||
    machineType.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  )
}

// Helper function to get human-readable state names
const getStateDisplayName = (state: string): string => {
  const stateNames: Record<string, string> = {
    inactive: 'Not Started',
    active: 'In Progress',
    scheduled: 'Appointment Scheduled',
    labsOrdered: 'Labs Ordered',
    labsReturned: 'Labs Signed',
    bookAppointment: 'Needs to Book Appointment',
    inAppointment: 'Appointment Booked',
    waitingForNextAppointment: 'Needs to Book Appointment',
  }

  return (
    stateNames[state] ||
    state.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  )
}

// Helper function to get action display names
const getActionDisplayName = (definition: string): string => {
  const actionNames: Record<string, string> = {
    'book-initial-md': 'Book Initial Medical Appointment',
    'book-initial-rd': 'Book Initial Nutrition Appointment',
    'complete-intake': 'Complete Intake Forms',
    'join-appointment': 'Join Appointment',
    'upload-labs': 'Upload Lab Results',
    'schedule-followup': 'Schedule Follow-up',
    'monthly-checkin': 'Monthly Check-in',
    'verify-insurance': 'Verify Insurance',
  }
  return (
    actionNames[definition] ||
    definition.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  )
}

const TimelineV2: React.FC<TimelineV2Props> = ({ patientId }) => {
  const { isLoading, error, data } = useTimelineV2(patientId)

  const groupedMachines = useMemo(() => {
    if (!data?.machineInstances) {
      return {}
    }

    return data.machineInstances.reduce((acc, machine) => {
      if (!acc[machine.machineType]) {
        acc[machine.machineType] = []
      }

      acc[machine.machineType].push(machine)
      return acc
    }, {} as Record<string, typeof data.machineInstances>)
  }, [data?.machineInstances])

  const activeVirtualActions = useMemo(() => {
    return (
      data?.virtualActions?.filter((action) => action.status === 'active') || []
    )
  }, [data?.virtualActions])

  // Get recent activity (last 7 days) - exclude patient-journey since it doesn't update frequently
  const recentActivity = useMemo(() => {
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    return (
      data?.machineInstances
        ?.filter(
          (machine) =>
            machine.machineType !== 'patient-journey' && // Exclude the main journey machine
            new Date(machine.updatedAt) > sevenDaysAgo
        )
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        ) || []
    )
  }, [data?.machineInstances])

  // Get journey insights - focus on actionable insights
  const journeyInsights = useMemo(() => {
    const insights = []

    if (activeVirtualActions.length === 0) {
      insights.push('Patient has no active tasks at this time')
    }

    if (activeVirtualActions.length > 3) {
      insights.push('Patient has multiple active tasks - may need support')
    }

    // Check for specific journey states that might indicate issues
    const appointmentMachines =
      data?.machineInstances?.filter((m) => m.machineType === 'appointment') ||
      []

    // Check for stuck appointment processes
    const stuckAppointments = appointmentMachines.filter(
      (m) =>
        m.currentState === 'active' &&
        new Date(m.updatedAt) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // 7 days ago
    )

    if (stuckAppointments.length > 0) {
      insights.push(
        `${stuckAppointments.length} appointment process(es) may be stuck - no recent updates`
      )
    }

    // Check for completed appointments without follow-up
    const completedAppointments = appointmentMachines.filter(
      (m) => m.currentState === 'completed'
    )
    const hasPostAppointmentMachine = data?.machineInstances?.some(
      (m) => m.machineType === 'post-appointment'
    )

    if (completedAppointments.length > 0 && !hasPostAppointmentMachine) {
      insights.push('Appointment completed but no follow-up process started')
    }

    // Check for labs that might be stuck
    const labMachines =
      data?.machineInstances?.filter((m) => m.machineType === 'labs') || []
    const stuckLabs = labMachines.filter(
      (m) =>
        m.currentState === 'active' &&
        new Date(m.updatedAt) < new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) // 14 days ago
    )

    if (stuckLabs.length > 0) {
      insights.push(
        `${stuckLabs.length} lab process(es) may be stuck - no recent updates`
      )
    }

    return insights
  }, [data?.machineInstances, activeVirtualActions])

  const renderContextDetails = (context: Record<string, unknown>) => {
    const details = []

    if (context.careType) {
      details.push(
        <Typography key="careType" variant="body2">
          Care Type: {context.careType as string}
        </Typography>
      )
    }

    if (context.appointmentType) {
      details.push(
        <Typography key="appointmentType" variant="body2">
          Appointment Type: {context.appointmentType as string}
        </Typography>
      )
    }

    if (context.providerId) {
      details.push(
        <Typography key="providerId" variant="body2">
          Provider ID: {context.providerId as string}
        </Typography>
      )
    }

    if (context.requiresIntake !== undefined) {
      details.push(
        <Typography key="requiresIntake" variant="body2">
          Intake Required: {context.requiresIntake ? 'Yes' : 'No'}
        </Typography>
      )
    }

    if (context.intakeCompleted !== undefined) {
      details.push(
        <Typography key="intakeCompleted" variant="body2">
          Intake Completed: {context.intakeCompleted ? 'Yes' : 'No'}
        </Typography>
      )
    }

    return details
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading patient journey...</Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">Error loading patient journey</Typography>
        </CardContent>
      </Card>
    )
  }

  if (!data?.enabled) {
    return (
      <Card>
        <CardContent>
          <Typography>
            {data?.message || 'Patient journey tracking is not enabled'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Patient Journey Overview
      </Typography>

      {/* Journey Insights */}
      {journeyInsights.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Journey Insights
            </Typography>
            <Box>
              {journeyInsights.map((insight, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  • {insight}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Activity (Last 7 Days)
            </Typography>
            <Grid container spacing={2}>
              {recentActivity.slice(0, 4).map((machine) => (
                <Grid item xs={12} md={6} key={machine.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {getJourneyDisplayName(machine.machineType)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Status changed to:{' '}
                        {getStateDisplayName(machine.currentState)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(machine.updatedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Current Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Actions ({activeVirtualActions.length})
          </Typography>
          {activeVirtualActions.length > 0 ? (
            <Grid container spacing={2}>
              {activeVirtualActions.map((action) => (
                <Grid item xs={12} md={6} key={action.id}>
                  <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {getActionDisplayName(action.definition)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip label="Active" color="primary" size="small" />
                        {action.provider && (
                          <Chip
                            label={`Dr. ${action.provider.name}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="textSecondary">
              No active actions at this time
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Journey Progress */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Journey Progress
          </Typography>

          {Object.entries(groupedMachines).map(([machineType, machines]) => (
            <Accordion key={machineType} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography variant="subtitle1">
                    {getJourneyDisplayName(machineType)}
                  </Typography>
                  <Chip label={machines.length} size="small" />
                  <Box flexGrow={1} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {machines.map((machine) => (
                    <Grid item xs={12} md={6} key={machine.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={2}
                          >
                            <Typography variant="body2">Status:</Typography>
                            <Chip
                              label={getStateDisplayName(machine.currentState)}
                              color={
                                machine.currentState === 'completed' ||
                                machine.currentState === 'labsReturned'
                                  ? 'success'
                                  : machine.currentState === 'active'
                                  ? 'primary'
                                  : 'default'
                              }
                              size="small"
                            />
                          </Box>

                          {/* Show relevant context information */}
                          {machine.context &&
                            Object.keys(machine.context).length > 0 && (
                              <Stack spacing={1}>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  gutterBottom
                                >
                                  Details:
                                </Typography>
                                {renderContextDetails(
                                  machine.context as Record<string, unknown>
                                )}
                                {machine.instanceId &&
                                  !machine.instanceId.includes('temp') && (
                                    <Typography variant="body2">
                                      {getInstanceDisplayName(
                                        machine.machineType
                                      )}
                                      : {machine.instanceId}
                                    </Typography>
                                  )}
                              </Stack>
                            )}

                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            Last Updated:{' '}
                            {new Date(machine.updatedAt).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>
    </Box>
  )
}

export default TimelineV2
